<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { trainingApi } from '../services/trainingApi'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import HistoryLogsTable from '@/components/HistoryLogsTable.vue'
import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'
import { useFormEmbeddedLayout } from '@/composables/ui/useFormEmbeddedLayout'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import type { IHistoryLog } from '@/types/IHistoryLog'

const props = withDefaults(defineProps<{ modelValue: boolean; title: string; snapshot?: string; busy?: boolean; nested?: boolean; historyKind?: string; entityId?: string }>(), { snapshot: '', busy: false, nested: false })
const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>()
const { smAndDown } = useDisplay()
const permissions = usePermissionsStore()
const layout = useFormEmbeddedLayout(computed(() => Boolean(props.entityId) && !props.nested))
const baseline = ref('')
const confirm = ref(false)
const showHistory = ref(false)
const logs = ref<IHistoryLog[]>([])
const historyPage = ref(1)
const historyNext = ref(false)
const historyLoading = ref(false)
const historyError = ref('')
const dialog = ref<{ globalTop: boolean } | null>(null)
watch(() => props.modelValue, open => {
  if (open) {
    baseline.value = props.snapshot
    showHistory.value = false
    logs.value = []
    historyPage.value = 1
  }
}, { immediate: true })
watch(() => props.snapshot, (value, previous) => {
  if (!value || !previous) baseline.value = value
})
function close() {
  if (props.busy) return
  if (props.snapshot !== baseline.value) confirm.value = true
  else emit('update:modelValue', false)
}
const dismiss = useDialogDismissGuard(close, 'TrainingDialog', () => Boolean(dialog.value?.globalTop))
function dismissOutside(event: Event) {
  if (event.target instanceof Element && event.target.closest('.entity-form-actions')) return
  dismiss(event)
}
async function loadHistory(more = false) {
  if (!props.entityId || !props.historyKind) return
  historyLoading.value = true
  historyError.value = ''
  const page = more ? historyPage.value + 1 : 1
  try {
    const data = await trainingApi.history(props.historyKind, props.entityId, page)
    logs.value = more ? [...logs.value, ...data.results] : data.results
    historyPage.value = page
    historyNext.value = Boolean(data.next)
  } catch {
    historyError.value = 'Не удалось загрузить историю.'
  } finally {
    historyLoading.value = false
  }
}
function toggleHistory() {
  showHistory.value = !showHistory.value
  if (showHistory.value) loadHistory()
}
defineExpose({
  acceptChanges: () => {
    baseline.value = props.snapshot
  },
})
</script>

<template>
  <Teleport to="body">
    <div v-if="modelValue && layout.isEmbedded.value" class="entity-form-backdrop" />
  </Teleport>
  <VDialog
    ref="dialog"
    :model-value="modelValue"
    :fullscreen="smAndDown"
    :max-width="layout.maxWidth.value"
    :content-class="layout.isEmbedded.value ? 'entity-form-embedded' : 'entity-form-create-dialog'"
    :scrim="!layout.isEmbedded.value"
    :z-index="nested ? 2500 : 2400"
    persistent
    @click:outside="dismissOutside"
    @keydown.esc.stop="dismiss"
  >
    <Teleport to="body">
      <div v-if="modelValue && layout.isEmbedded.value" class="entity-form-actions">
        <VBtn
          v-if="entityId && historyKind && permissions.canViewChangeHistory"
          class="entity-form-action-btn"
          color="primary"
          icon="ri-history-line"
          aria-label="История"
          :title="showHistory ? 'Скрыть историю' : 'Показать историю'"
          @click="toggleHistory"
        />
      </div>
    </Teleport>
    <VCard class="training-dialog-card" :class="{ 'entity-form-card h-100': layout.isEmbedded.value }">
      <VCardTitle class="d-flex align-start gap-3 flex-shrink-0 pa-6" :class="{ 'px-4 py-3': smAndDown }">
        <span class="training-dialog-title text-h4">{{ title }}</span><VSpacer />
        <VBtn
          v-if="!layout.isEmbedded.value && entityId && historyKind && permissions.canViewChangeHistory"
          variant="text"
          icon="ri-history-line"
          aria-label="История"
          @click="toggleHistory"
        />
        <CustomCloseButton aria-label="Закрыть карточку" :close-action="close" />
      </VCardTitle>
      <VDivider />
      <div class="entity-form-body training-dialog-body" :class="{ 'flex-column': smAndDown }">
        <div class="entity-form-scroll-body training-dialog-content pa-4">
          <slot />
        </div>
        <aside v-if="showHistory" class="entity-form-scroll-body training-history pa-4">
          <h3 class="text-h6 mb-4">
            История изменений
          </h3>
          <VProgressLinear v-if="historyLoading" indeterminate />
          <VAlert v-if="historyError" type="error" variant="tonal">
            {{ historyError }}<VBtn variant="text" @click="loadHistory">
              Повторить
            </VBtn>
          </VAlert>
          <HistoryLogsTable :logs="logs" />
          <p v-if="!historyLoading && !logs.length && !historyError" class="text-medium-emphasis">
            История появится после новых изменений.
          </p>
          <VBtn
            v-if="historyNext"
            :loading="historyLoading"
            variant="text"
            @click="loadHistory(true)"
          >
            Показать ещё
          </VBtn>
        </aside>
      </div>
      <VDivider />
      <footer class="training-dialog-footer d-flex align-center flex-wrap flex-shrink-0 gap-2 pa-4">
        <VBtn
          color="secondary"
          variant="outlined"
          :disabled="busy"
          @click="close"
        >
          {{ snapshot ? 'Отмена' : 'Закрыть' }}
        </VBtn><VSpacer /><slot name="actions" />
      </footer>
    </VCard>
  </VDialog>
  <ConfirmDialog
    v-model:is-dialog-visible="confirm"
    confirmation-question="Закрыть без сохранения?"
    confirmation-text="Введённые изменения будут потеряны."
    confirm-btn="Закрыть"
    reject-btn="Продолжить редактирование"
    @confirm="value => { confirm = false; if (value) emit('update:modelValue', false) }"
  />
</template>

<style scoped>
.training-dialog-card { display: flex; flex-direction: column; min-height: 0; max-height: 100%; }
.training-dialog-title { white-space: normal; overflow-wrap: anywhere; min-width: 0; }
.training-dialog-body { display: flex; flex: 1 1 auto; min-height: 0; overflow: hidden; }
.training-dialog-content { flex: 1 1 60%; min-width: 0; }
.training-history { flex: 1 1 40%; min-width: 0; border-inline-start: 1px solid rgba(var(--v-border-color), var(--v-border-opacity)); }
@media (max-width: 959px) {
  /* More specific than the shared create-dialog body, which clips overflow. */
  .training-dialog-card > .entity-form-body.training-dialog-body { overflow-y: auto; }
  .training-dialog-content, .training-history { overflow: visible; flex: none; }
}
@media (max-width: 599px) { .training-dialog-footer :deep(.v-btn--variant-elevated) { inline-size: 100%; } }
</style>
